import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getRequestPaginationSolicitationThunk } from 'redux/Reducers/Concierge/requestPaginationSolicitationSlicer';
import { conciergeLoginThunk } from 'redux/Reducers/Concierge/conciergeLoginSlicer';

import 'Assets/styles/default/pages/concierge/ListConciergeStyles.less';

import { Col, Row } from 'antd';
import { RightOutlined } from '@ant-design/icons';

import {
    Form,
    Table,
    Typography,
    VertBreadcrumb,
    VertButton,
    VertContainer,
    VertIcon,
    VertInput,
    VertLoading,
    VertSelect,
    VertTokens,
} from '@vertem-core/vertice-ds';

import EmptyIcon from 'components/Concierge/Icons/empty';
import StatusIcon from 'components/Concierge/Icons/status';
import ErrorApiModal from 'components/Concierge/Modals/ErrorApiModal';

import { ConciergeBanners, SolicitationsStatusFilterOptions } from 'Constants/Concierge';
import { ListSolicitationsInitialFilterState } from 'Constants/Concierge/initialStateValues';

const ListConciergePage = () => {
    const [listConcierge, setListConcierge] = useState([]);
    const [isOpenModalErrorApi, setIsOpenModalErrorApi] = useState(false);
    const [termDebouncer, setTermDebouncer] = useState(null);
    const [solicitationListFilter, setSolicitationListFilter] = useState(ListSolicitationsInitialFilterState);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { designTokens } = useContext(VertTokens);
    const { 'banners-concierge': banners } = designTokens;

    const GetStatusColor = (status) => {
        switch (status) {
            case 1:
                return 'var(--color-success)';
            case 2:
                return 'var(--color-yellow)';
            case 3:
                return 'var(--color-neutral-light)';
            case 4:
                return 'var(--color-red)';
        }
    }

    const columns = [
        {
            title: 'Nº da solicitação',
            dataIndex: 'solicitationNumber',
            align: 'center'
        },
        {
            title: "Data da solicitação",
            dataIndex: "solicitationDate",
            align: "center",
            render: (_, { solicitationDate }) => (
                <div className='text-align-center'>
                    {
                        solicitationDate 
                            ? new Date(solicitationDate).toLocaleString('pt-BR', { dateStyle: "short" }) 
                            : '-'
                    }
                </div>
            ),
        },
        {
            title: 'Solicitante',
            dataIndex: 'requester',
            align: 'center'
        },
        {
            title: 'Participante',
            dataIndex: 'participant',
            align: 'center'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            align: 'center',
            render: (_, { status, statusDescription }) => (
                <Row justify={"center"}>

                    <Col className="align-items-center">
                        <StatusIcon color={GetStatusColor(status)} />
                    </Col>

                    <Col>
                        <Typography.Text className='span-cell-content cell-status'>
                            {statusDescription}
                        </Typography.Text>
                    </Col>

                </Row>
            ),
        },
        {
            title: '',
            dataIndex: 'key',
            align: 'center',
            render: (key) => (
                <Row justify={'center'} align={'middle'}>
                    <Col>
                        <VertButton
                            type='highlight'
                            className='vert-two-fa-trigger button-manage-solicitation button-concierge'
                            size="small"
                            onClick={() => editSolicitation(key)}
                        >
                            Gerenciar solicitação
                        </VertButton>
                    </Col>
                </Row>
            )
        }
    ];

    const handleTermFilter = (term) => {
        if (termDebouncer) clearTimeout(termDebouncer);

        if (!term) {
            setSolicitationListFilter({ ...solicitationListFilter, term: null });
            setTermDebouncer(null);
            return;
        }

        const timeoutId = setTimeout(() => {
            setSolicitationListFilter({ ...solicitationListFilter, term, current: 1 });
        }, 300);

        setTermDebouncer(timeoutId);
    }

    const handleStatusFilterSelection = (value) => {
        setSolicitationListFilter({ ...solicitationListFilter, statusId: value, current: 1 })
    }

    const editSolicitation = (id) => {
        navigate(`/manage-solicitation/${id}`);
    }

    const {
        errorConciergeLogin,
    } = useSelector(
        (state) => state.ConciergeLogin
    );

    const {
        dataRequestPaginationSolicitation,
        loadingRequestPaginationSolicitation,
    } = useSelector(
        (state) => state.RequestPaginationSolicitation
    );

    const handlePagination = (pagination) => {
        setSolicitationListFilter({ ...solicitationListFilter, current: pagination.current });
    }

    useEffect(() => {
        dispatch(conciergeLoginThunk());
    }, []);

    useEffect(() => {
        if (errorConciergeLogin)
            setIsOpenModalErrorApi(true);
    }, [errorConciergeLogin]);

    useEffect(() => {
        dispatch(getRequestPaginationSolicitationThunk(solicitationListFilter));
    }, [solicitationListFilter]);

    useEffect(() => {
        if (dataRequestPaginationSolicitation)
            setListConcierge(dataRequestPaginationSolicitation.data);
    }, [dataRequestPaginationSolicitation]);

    const renderSolicitations = () => {
        if (loadingRequestPaginationSolicitation) {
            return (
                <Row className="loading-container" justify={"center"}>
                    <VertLoading />
                </Row>   
            );
        }

        if (listConcierge && listConcierge.length === 0) {
            return (
                <Row className="concierge-solicitation-not-found" gutter={8}>
                    <Col span={24}>
                        <Row justify="center">
                            <EmptyIcon />
                        </Row>
                    </Col>
                    <Col span={24}>
                        <Row justify="center">
                            <Typography.Text className='text-title text-not-found'>
                                Nenhuma solicitação de produto/serviço foi encontrada.
                            </Typography.Text>
                        </Row>
                    </Col>
                </Row>
            )
        }

        return (
            <>
                <div className='container-list-concierge-title'>
                    <Typography.Text className='list-concierge-title'>
                        Suas solicitações
                    </Typography.Text>
                </div>

                <div className='container-table'>
                    <Table
                        onChange={handlePagination}
                        className='table-concierge'
                        pagination={{
                            showSizeChanger: false,
                            current: solicitationListFilter.current,
                            pageSize: solicitationListFilter.pageSize,
                            total: dataRequestPaginationSolicitation.total,
                            position: ['bottomCenter']
                        }}
                        bordered
                        columns={columns}
                        dataSource={listConcierge}
                        scroll={{ x: "1024px" }}
                    />
                </div>
            </>
        )
    }

    return (
        <>
            <VertContainer spacingX="regular" spacingY="regular">
                <VertBreadcrumb
                    hasBackButton={true}
                    separator={<VertIcon component={RightOutlined} />}
                    items={[
                        {
                            title: 'Início',
                            path: '/',
                        },
                        {
                            title: 'Solicitação de produtos e serviços',
                            path: '/list-solicitations',
                        }
                    ]}
                />

                <img className='container-banner-list-concierge'
                    src={banners && banners['solicitation-list'] 
                            ? banners['solicitation-list'] 
                            : ConciergeBanners.SolicitationsListPage.Default}
                />

                <div className='container-info-steps-solicition'>

                    <Row>

                        <Col span={24} >
                            <Typography.Paragraph className='info-steps-solicition-title'>
                                Como funciona?
                            </Typography.Paragraph>
                        </Col>

                        <Col span={24}>
                            <Typography.Paragraph className='info-steps-solicition-subtitle'>
                                Aqui você consegue fazer solicitações de produtos e serviços fora do catálogo como: consultorias, máquinas para sua empresa etc.
                            </Typography.Paragraph>
                        </Col>

                        <Col xs={24} sm={24} lg={4}>

                            <Typography.Text className='info-steps-solicition-number'>
                                1
                            </Typography.Text>

                            <Typography.Text className='info-steps-solicition-description'>
                                Selecionar modalidade
                            </Typography.Text>

                        </Col>

                        <Col xs={24} sm={24} lg={4}>
                            <Typography.Text className='info-steps-solicition-number'>
                                2
                            </Typography.Text>
                            <Typography.Text className='info-steps-solicition-description'>
                                Detalhar solicitação
                            </Typography.Text>
                        </Col>

                        <Col xs={24} sm={24} lg={4}>
                            <Typography.Text className='info-steps-solicition-number'>
                                3
                            </Typography.Text>
                            <Typography.Text className='info-steps-solicition-description'>
                                Escolher fornecedor
                            </Typography.Text>
                        </Col>

                        <Col xs={24} sm={24} lg={4}>
                            <Typography.Text className='info-steps-solicition-number'>
                                4
                            </Typography.Text>
                            <Typography.Text className='info-steps-solicition-description'>
                                Informar endereço
                            </Typography.Text>
                        </Col>

                        <Col xs={24} sm={24} lg={4}>
                            <Typography.Text className='info-steps-solicition-number'>
                                5
                            </Typography.Text>
                            <Typography.Text className='info-steps-solicition-description'>
                                Gerenciar pedido
                            </Typography.Text>
                        </Col>

                    </Row>

                </div>

                <Row className='container-buttons'>
                    <Col>
                        <VertButton
                            onClick={() => { navigate('/new-solicitation') }}
                            type='highlight'
                            className='vert-two-fa-trigger button-concierge-list button-concierge'
                            size="default"
                        >
                            <Typography.Text className='span-button-concierge-list span-button-cadastrar'>
                                Criar Nova Solicitação
                            </Typography.Text>

                        </VertButton>
                    </Col>
                </Row>

                <Row className='margin-top-extra-big' align={'middle'} justify={'space-between'}>

                    <Col xs={24} sm={24} md={8}>
                        <Form.Item noStyle>
                            <VertInput
                                label="Buscar por Nº da solicitação ou envolvidos"
                                onChange={(e) => handleTermFilter(e.target.value)}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={4}>
                        <Form.Item noStyle >
                            <VertSelect 
                                className='select-category' 
                                placeholder='Filtrar por status'
                                value={solicitationListFilter.statusId}
                                onSelect={handleStatusFilterSelection}
                                items={SolicitationsStatusFilterOptions} 
                            />
                        </Form.Item>
                    </Col>
                </Row>

                { renderSolicitations() }

                <ErrorApiModal
                    isOpen={isOpenModalErrorApi}
                    onClose={() => navigate("/auth/logout")}
                    subtitle="Será necessário realizar o login novamente!"
                />

            </VertContainer >
        </>
    )
}

export default ListConciergePage;
